/**
 * United States and Canada `country_key` GUIDs from the Jemma countries codeset. These are the
 * only two countries with a curated state/province list in this POC. The GUIDs mirror the
 * dashboard's `PRIORITIZED_COUNTRIES_BY_ID`/`STATES_DATA_SET` keys
 * (`libs/shared/src/constants/states.constant.ts`) 1:1, since both apps read from the same Jemma
 * codeset — duplicated locally (rather than imported) because this widget has no dependency on
 * the host app's workspace libraries.
 */
export const UNITED_STATES_COUNTRY_KEY = '7d15c4bd84f84deff58d573f4b507c15';
export const CANADA_COUNTRY_KEY = '08eaaa992ef23176aaf615b98e147e18';

/** Order matters: countries in this set are pinned to the top of the dropdown, in insertion order (US, then Canada). */
export const PRIORITIZED_COUNTRY_KEYS: ReadonlySet<string> = new Set([UNITED_STATES_COUNTRY_KEY, CANADA_COUNTRY_KEY]);

const US_STATES = [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'Florida',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming'
] as const;

const CANADIAN_PROVINCES = [
    'Alberta',
    'British Columbia',
    'Manitoba',
    'New Brunswick',
    'Newfoundland',
    'Northwest Territories',
    'Nova Scotia',
    'Nunavut',
    'Ontario',
    'Prince Edward Island',
    'Quebec',
    'Saskatchewan'
] as const;

/**
 * Maps a country's `country_key` to its list of state/province names, mirroring the dashboard's
 * `STATES_DATA_SET`/`wcg-address` behavior: countries present here render a state/province
 * dropdown; every other country falls back to a free-text state/province input.
 */
export const COUNTRY_STATES: ReadonlyMap<string, readonly string[]> = new Map<string, readonly string[]>([
    [UNITED_STATES_COUNTRY_KEY, US_STATES],
    [CANADA_COUNTRY_KEY, CANADIAN_PROVINCES]
]);

/**
 * Sorts a list of countries so United States and Canada appear first (in that order), mirroring
 * the dashboard's `sortArrayOfObjectsBasingOnUniqueSet(countries, 'id', PRIORITIZED_COUNTRIES_BY_ID)`
 * (`organization.facade.service.ts`'s `$sortedCountries`).
 */
export function sortCountriesByPriority<T extends { country_key: string }>(countries: T[]): T[] {
    const _priorityOrder = [...PRIORITIZED_COUNTRY_KEYS];

    return countries.toSorted((a, b) => {
        const _aPriority = PRIORITIZED_COUNTRY_KEYS.has(a.country_key);
        const _bPriority = PRIORITIZED_COUNTRY_KEYS.has(b.country_key);

        if (_aPriority !== _bPriority) {
            return _aPriority ? -1 : 1;
        }

        if (_aPriority && _bPriority) {
            return _priorityOrder.indexOf(a.country_key) - _priorityOrder.indexOf(b.country_key);
        }

        return 0;
    });
}
